import type { LucideIcon } from 'lucide-react'
import type { PublicResearchMatchDetailResponse } from '~/features/worldcup-research/types'
import { Card } from '@heroui/react'
import { ArrowLeftRight, CircleDot, RectangleVertical, Square, Video } from 'lucide-react'
import { useTranslations } from 'use-intl'
import { resolveWorldcupTeamLogo } from '~/features/worldcup/team-logos'
import { StateText, translateOptional } from './components'

type MatchTimelineEvent = NonNullable<PublicResearchMatchDetailResponse['timeline']>[number]
type MatchMarketSignals = NonNullable<PublicResearchMatchDetailResponse['marketSignals']>

const EVENT_STYLE: Record<MatchTimelineEvent['type'], { labelKey: string, className: string, Icon: LucideIcon }> = {
  goal: {
    labelKey: 'eventTypes.goal',
    className: 'border-success/30 bg-success/10 text-success',
    Icon: CircleDot,
  },
  card: {
    labelKey: 'eventTypes.card',
    className: 'border-warning/40 bg-warning/10 text-warning',
    Icon: Square,
  },
  subst: {
    labelKey: 'eventTypes.subst',
    className: 'border-primary/25 bg-primary/10 text-primary',
    Icon: ArrowLeftRight,
  },
  var: {
    labelKey: 'eventTypes.var',
    className: 'border-secondary/30 bg-secondary/10 text-secondary',
    Icon: Video,
  },
}

function TeamTimelineBadge({
  label,
  logoUrl,
  tone,
}: {
  label: string
  logoUrl: string | null
  tone: 'home' | 'away'
}) {
  return (
    <span className={`inline-flex min-w-0 items-center gap-2 rounded-full px-2 py-1 ${tone === 'home' ? 'bg-primary/10 text-primary' : 'bg-secondary/10 text-secondary'}`}>
      {logoUrl && (
        <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-md border border-divider bg-background shadow-sm">
          <img src={logoUrl} alt="" className="size-5 object-contain" loading="lazy" />
        </span>
      )}
      <span className="min-w-0 truncate">{label}</span>
    </span>
  )
}

function eventMinuteLabel(event: MatchTimelineEvent) {
  return `${event.minute}'${event.extraMinute ? `+${event.extraMinute}` : ''}`
}

function eventSummary(event: MatchTimelineEvent) {
  const actor = event.playerName ?? event.teamName
  return event.assistName ? `${actor} / ${event.assistName}` : actor
}

function eventKey(event: MatchTimelineEvent) {
  return [
    event.minute,
    event.extraMinute ?? 0,
    event.type,
    event.teamName,
    event.playerName ?? '',
    event.assistName ?? '',
    event.detail,
  ].join(':')
}

function eventSortValue(event: MatchTimelineEvent) {
  return event.minute * 100 + (event.extraMinute ?? 0)
}

function eventVisual(event: MatchTimelineEvent) {
  if (event.type !== 'card')
    return EVENT_STYLE[event.type]

  const detail = event.detail.toLowerCase()
  if (detail.includes('red')) {
    return {
      ...EVENT_STYLE.card,
      className: 'border-danger/35 bg-danger/10 text-danger',
      Icon: RectangleVertical,
    }
  }

  return EVENT_STYLE.card
}

export function TimelinePanel({
  events,
  homeTeam,
  awayTeam,
  homeTeamLabel = homeTeam,
  awayTeamLabel = awayTeam,
}: {
  events: MatchTimelineEvent[]
  homeTeam: string
  awayTeam: string
  homeTeamLabel?: string
  awayTeamLabel?: string
}) {
  const t = useTranslations('ResearchWorkspace.matchDetail')
  const formatTimelineTeam = (teamName: string) => {
    if (teamName === homeTeam)
      return homeTeamLabel
    if (teamName === awayTeam)
      return awayTeamLabel
    return teamName
  }
  const timelineEntries = [...events]
    .sort((left, right) => eventSortValue(left) - eventSortValue(right))
    .map((event, order) => ({
      event,
      key: `${eventKey(event)}:${order}`,
    }))
  const homeTeamLogoUrl = resolveWorldcupTeamLogo({ teamName: homeTeam })
  const awayTeamLogoUrl = resolveWorldcupTeamLogo({ teamName: awayTeam })

  if (events.length === 0) {
    return (
      <Card className="min-w-0 scroll-mt-20">
        <Card.Header><Card.Title>{translateOptional(t, 'timeline.title', 'Match Timeline')}</Card.Title></Card.Header>
        <Card.Content className="min-w-0">
          <StateText>{translateOptional(t, 'timeline.empty', 'No match events available.')}</StateText>
        </Card.Content>
      </Card>
    )
  }
  return (
    <Card className="min-w-0 scroll-mt-20">
      <Card.Header>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Card.Title>{translateOptional(t, 'timeline.title', 'Match Timeline')}</Card.Title>
            <p className="mt-1 text-sm text-muted">{translateOptional(t, 'timeline.subtitle', 'Goals, cards, substitutions, and VAR checks in match order.')}</p>
          </div>
          <div className="flex flex-wrap gap-1.5 text-xs font-semibold text-muted">
            {(['goal', 'card', 'subst', 'var'] as const).map((type) => {
              const style = EVENT_STYLE[type]
              const Icon = style.Icon
              return (
                <span key={type} className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 ${style.className}`}>
                  {type === 'card'
                    ? (
                        <>
                          <Square className="size-3 text-warning" aria-hidden="true" />
                          <RectangleVertical className="size-3 text-danger" aria-hidden="true" />
                        </>
                      )
                    : <Icon className="size-3" aria-hidden="true" />}
                  {translateOptional(t, `timeline.${style.labelKey}`, type)}
                </span>
              )
            })}
          </div>
        </div>
      </Card.Header>
      <Card.Content className="min-w-0">
        <div
          className="overflow-hidden rounded-lg border border-divider bg-background shadow-sm"
          aria-label={translateOptional(t, 'timeline.mobileListLabel', 'Chronological match events')}
        >
          <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] sm:grid-cols-[7rem_minmax(0,1fr)]">
            <div className="grid grid-rows-[6.25rem_3rem_6.25rem] border-r border-divider bg-default-50/80 text-xs font-black text-foreground">
              <div className="flex items-center px-3">
                <TeamTimelineBadge label={homeTeamLabel} logoUrl={homeTeamLogoUrl} tone="home" />
              </div>
              <div className="border-y border-divider bg-background/70" aria-hidden="true" />
              <div className="flex items-center px-3">
                <TeamTimelineBadge label={awayTeamLabel} logoUrl={awayTeamLogoUrl} tone="away" />
              </div>
            </div>
            <div className="relative overflow-x-auto overflow-y-hidden [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-default-300 [&::-webkit-scrollbar-track]:bg-transparent">
              <div className="relative min-w-max px-6">
                <div className="pointer-events-none absolute left-6 right-6 top-[7.75rem] z-0 h-1.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-primary/25 via-primary to-primary/25 shadow-sm" aria-hidden="true" />
                <div className="flex min-w-max">
                  {timelineEntries.map(({ event, key }) => {
                    const isHome = event.teamName === homeTeam
                    const style = eventVisual(event)
                    const Icon = style.Icon
                    const label = `${eventMinuteLabel(event)} ${formatTimelineTeam(event.teamName)} ${eventSummary(event)} ${event.detail}`
                    return (
                      <div key={key} className="grid w-36 shrink-0 grid-rows-[6.25rem_3rem_6.25rem] px-2">
                        <div className={`flex min-w-0 items-end justify-center pb-2 ${isHome ? '' : 'opacity-0'}`} aria-hidden={!isHome}>
                          {isHome && (
                            <div className="flex min-w-0 max-w-full flex-col items-center text-center" title={label}>
                              <span className={`inline-flex size-10 items-center justify-center rounded-full border shadow-md ring-4 ring-background ${style.className}`}>
                                <Icon className="size-4" aria-hidden="true" />
                              </span>
                              <span className="mt-1 line-clamp-2 max-w-full break-words text-[11px] font-bold leading-tight text-foreground">
                                {eventSummary(event)}
                              </span>
                              <span className="mt-1 h-4 w-px bg-primary/35" aria-hidden="true" />
                            </div>
                          )}
                        </div>
                        <div className="relative flex min-w-0 items-center justify-center">
                          <span className="absolute left-1/2 top-1/2 z-10 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-primary shadow-sm" aria-hidden="true" />
                          <div className="relative z-20 mt-9 flex flex-col items-center">
                            <span className={`inline-flex min-w-11 justify-center rounded-full border bg-background px-2 py-1 font-mono text-xs font-black shadow-sm ring-2 ring-background ${style.className}`}>
                              {eventMinuteLabel(event)}
                            </span>
                          </div>
                        </div>
                        <div className={`flex min-w-0 items-start justify-center pt-2 ${isHome ? 'opacity-0' : ''}`} aria-hidden={isHome}>
                          {!isHome && (
                            <div className="flex min-w-0 max-w-full flex-col items-center text-center" title={label}>
                              <span className="mb-1 h-4 w-px bg-primary/35" aria-hidden="true" />
                              <span className={`inline-flex size-10 items-center justify-center rounded-full border shadow-md ring-4 ring-background ${style.className}`}>
                                <Icon className="size-4" aria-hidden="true" />
                              </span>
                              <span className="mt-1 line-clamp-2 max-w-full break-words text-[11px] font-bold leading-tight text-foreground">
                                {eventSummary(event)}
                              </span>
                            </div>
                          )}
                        </div>
                        <span className="sr-only">{label}</span>
                        <span className="sr-only">{event.detail}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end border-t border-divider bg-default-50/70 px-3 py-2 text-[11px] font-semibold text-muted sm:px-4">
            <div className="font-mono tabular-nums">
              0'
              <span className="mx-2 text-divider">|</span>
              45'
              <span className="mx-2 text-divider">|</span>
              90'
            </div>
          </div>
        </div>
      </Card.Content>
    </Card>
  )
}

export function MarketSignalsPanel({ signals }: { signals: MatchMarketSignals }) {
  const t = useTranslations('ResearchWorkspace.matchDetail')
  if (signals.status === 'unavailable') {
    return (
      <Card className="scroll-mt-20">
        <Card.Header><Card.Title>{translateOptional(t, 'marketSignals.title', 'Prediction Market Signals')}</Card.Title></Card.Header>
        <Card.Content>
          <StateText>{translateOptional(t, 'marketSignals.unavailable', 'Prediction market data is not available for this match.')}</StateText>
        </Card.Content>
      </Card>
    )
  }
  return (
    <Card className="scroll-mt-20">
      <Card.Header>
        <div>
          <Card.Title>{translateOptional(t, 'marketSignals.title', 'Prediction Market Signals')}</Card.Title>
          <p className="mt-1 text-sm text-muted">{translateOptional(t, 'marketSignals.subtitle', 'Historical implied probability and bookmaker odds.')}</p>
        </div>
      </Card.Header>
      <Card.Content className="space-y-4">
        {signals.preMatchImpliedProbability && (
          <div>
            <h4 className="mb-2 text-sm font-semibold">{translateOptional(t, 'marketSignals.impliedProbability', 'Pre-match Implied Probability')}</h4>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              {(['home', 'draw', 'away'] as const).map(side => (
                <div key={side} className="rounded-md bg-default-100 p-2">
                  <div className="text-xs text-muted">{translateOptional(t, `marketSignals.${side}`, side)}</div>
                  <div className="font-mono font-bold">
                    {signals.preMatchImpliedProbability![side] != null
                      ? `${(signals.preMatchImpliedProbability![side]! * 100).toFixed(1)}%`
                      : '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {signals.finalOdds && (
          <div>
            <h4 className="mb-2 text-sm font-semibold">{translateOptional(t, 'marketSignals.finalOdds', 'Final Odds')}</h4>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              {(['home', 'draw', 'away'] as const).map(side => (
                <div key={side} className="rounded-md bg-default-100 p-2 font-mono">
                  {signals.finalOdds![side]?.toFixed(2) ?? '—'}
                </div>
              ))}
            </div>
            {signals.finalOdds.source && (
              <p className="mt-1 text-xs text-muted">
                {translateOptional(t, 'marketSignals.source', 'Source')}
                :
                {' '}
                {signals.finalOdds.source}
              </p>
            )}
          </div>
        )}
      </Card.Content>
    </Card>
  )
}
